// src/views/ConfigurarTarjeta/FiltroConfig.jsx 
import React from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import TarjetaAutocomplete from "../../../components/autocomplete/TarjetasAutocomplete";




/* ===========================
 * Autocomplete Patente (ahora devuelve {label, vehiculoId})
 * =========================== */
function AutocompletePatente({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState(
    typeof value === "string" ? value : (value?.label || "")
  );
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [page, setPage] = React.useState(0);

  const size = 20;
  const abortRef = React.useRef(null);
  const seqRef = React.useRef(0);
  const cancel = () => { try { abortRef.current?.abort(); } catch {} };

  const fetchAuto = React.useCallback(
    async ({ q, page: p }) => {
      cancel();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const seq = ++seqRef.current;

      if (!p || p === 0) { setItems([]); setHasMore(false); }
      setLoading(true);
      try {
        const url =
          `${apiRoutes.vehiculosConTarjeta.autocomplete}` +
          `?tipo=patente&q=${encodeURIComponent(q ?? "")}` +
          `&page=${encodeURIComponent(p ?? 0)}` +
          `&size=${encodeURIComponent(size)}`;
        const data = await apiFetchJson(url, { signal: ctrl.signal });
        if (seq !== seqRef.current) return;
        const mapped = (data?.items ?? [])
          .map((d) => ({ id: d.id ?? d.vehiculoId ?? null, label: d.patente || "" }))
          .filter((x) => x.label);
        setItems((prev) => (p && p > 0 ? [...prev, ...mapped] : mapped));
        setHasMore(Boolean(data?.hasMore));
        setPage(data?.page ?? p ?? 0);
      } catch (e) {
        if (e.name !== "AbortError") { setItems([]); setHasMore(false); }
      } finally { setLoading(false); }
    },
    [size],
  );

  React.useEffect(() => () => cancel(), []);

  // 👉 abrir/buscar únicamente con ≥ 2 caracteres
  const handleType = (txt) => {
    setInput(txt);
    if (value) onChange("");
    const q = (txt ?? "").trim();
    if (q.length >= 2) {
      if (!open) setOpen(true);
      fetchAuto({ q, page: 0 });
    } else {
      setOpen(false);
      setItems([]);
      setHasMore(false);
      setPage(0);
    }
  };

  // Scroll infinito (sin botón “Cargar más”)
  const onScrollMore = (e) => {
    if (!hasMore || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const q = (input ?? "").trim();
      if (q.length >= 2) fetchAuto({ q, page: (page ?? 0) + 1 });
    }
  };

  return (
    <div className="relative">
      <textarea
        rows={1}
        className="input rounded-2xl"
        placeholder="Descripción, modelo o texto libre..."
        value={input}
        onFocus={() => { /* no abrir por foco */ }}
        onChange={(e) => handleType(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />
      {open && (
        <div
          className="absolute z-20 mt-1 w-full border rounded bg-white shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* 🔥 Sin cabecera: lista directa, como en tarjeta */}
          <div  className="dropdown-panel max-h-60"  onScroll={onScrollMore}>
            {items.map((it, i) => (
              <button
                key={`${it.label}-${i}`}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50"
                onClick={() => {
                  onChange({ label: it.label, vehiculoId: it.id ?? null });
                  setInput(it.label);
                  setOpen(false);
                }}
              >
                <div className="text-sm text-gray-900">{it.label}</div>
              </button>
            ))}

            {!loading && items.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-500">Sin resultados</div>
            )}
            {loading && (
              <div className="px-3 py-3 text-sm text-gray-500">Cargando…</div>
            )}
          </div>
          {/* ❌ Se eliminó el botón “Cargar más” */}
        </div>
      )}
    </div>
  );
}


/* ===========================
 * Panel de filtros (EXPORT)
 * =========================== */
export default function FiltroConfig({
  onClose = () => {},
  onAplicar = () => {},
  inicial = null, // { clienteId, clienteNombre, tarjetaSel:{id,label,vehiculoId}, tarjetaId, vehiculoId, patente }
}) {
  // Cerrar con ESC
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Estado de filtros
  const [clienteSel, setClienteSel] = React.useState(
    inicial?.clienteId
      ? {
          id: Number(inicial.clienteId),
          nombre: inicial.clienteNombre ?? "",
        }
      : null
  );
  const [tarjetaSel, setTarjetaSel] = React.useState(inicial?.tarjetaSel ?? null);
  const [patente, setPatente] = React.useState(inicial?.patente ?? "");

  // IDs persistentes (para reabrir y aplicar sin tocar nada)
  const [vehiculoIdSel, setVehiculoIdSel] = React.useState(inicial?.vehiculoId ?? null);
  const [tarjetaIdSel, setTarjetaIdSel]   = React.useState(inicial?.tarjetaId ?? null);

  const limpiar = () => {
    setClienteSel(null);
    setTarjetaSel(null);
    setPatente("");
    setVehiculoIdSel(null);
    setTarjetaIdSel(null);
  };

  const aplicar = () => {
    onAplicar({
      clienteId: clienteSel?.id ?? null,
      clienteNombre: clienteSel?.nombre ?? null,

      // UI only
      patente: (patente ?? "").trim() || null,

      // Vehículo (prioriza el ID persistido; si no, el de la tarjeta seleccionada)
      vehiculoId: (vehiculoIdSel ?? tarjetaSel?.vehiculo?.id) ?? null,
      vehiculoIdsCsv:
        (vehiculoIdSel ?? tarjetaSel?.vehiculoId) != null
          ? String(vehiculoIdSel ?? tarjetaSel?.vehiculoId)
          : null,

      // Número e ID de tarjeta
      tarjetaNumero: tarjetaSel?.numero ?? null,
      tarjetaId: tarjetaSel?.id ?? null,
    });
  };

  return (
    <div className="drawer-backdrop"
      onClick={onClose}
     >
      {/* Panel derecho */}
      <div
        className="drawer-panel overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button
            onClick={onClose}
            className="btn-cancel"
            title="Cerrar"
          >
            &times;
          </button>
        </div>

        {/* Contenido */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            aplicar();
          }}
          className="drawer-body">
        
          {/* Cliente */}
          <div>
            <label className="form-label-fil">Cliente</label>
            <div className="w-full max-w-[500px]">
              <ClienteAutocomplete
                value={clienteSel}
                onChange={setClienteSel}
                placeholder="Buscar cliente..."
              />
            </div>
          </div>

          {/* Tarjeta (número) */}
          <div>
            <label className="form-label-fil">Tarjeta</label>
            <TarjetaAutocomplete
              value={tarjetaSel}
              onChange={(t) => {
                setTarjetaSel(t);
                setTarjetaIdSel(t?.id ?? null);

                // sincronizar vehículo si viene
                if (t?.vehiculo?.id != null) {
                  setVehiculoIdSel(t.vehiculo.id);
                }
              }}
              placeholder="Buscar tarjeta..."
              estadoId={29} // solo activas
            />
          </div>

          {/* Patente */}
          <div>
            <label className="form-label-fil">Patente</label>
            <AutocompletePatente
              value={patente}
              onChange={(v) => {
                if (typeof v === "string") {
                  setPatente(v);
                } else {
                  setPatente(v?.label ?? "");
                  if (v?.vehiculoId != null) setVehiculoIdSel(v.vehiculoId);
                }
              }}
            />
          </div>

          {/* Acciones */}
          <div className="text-right">
            <button type="submit" className="btn">
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
